<template>
  <v-navigation-drawer
    v-if="featureEnabled"
    right
    app
    temporary
    :value="drawerOpen"
    class="pay-range-version-drawer"
    width="80vw"
    data-test="version-drawer"
    @input="onDrawerInput"
  >
    <div class="pay-range-version-drawer__panel">
      <PayRangeDrawerHeader
        :version-code="versionCode"
        :status="status"
        :state-badge="stateBadge"
        :show-badges="true"
        :show-period-bar="true"
        period-bar-state="draft"
        :period-bar-label="periodLabel"
        :open-ended="true"
        :count="chatterCount"
        @close="requestClose"
        @chatter="onChatter"
      />

      <div class="pay-range-version-drawer__body">
        <div
          v-if="correctingLabel"
          class="pay-range-version-drawer__alert pay-range-version-drawer__alert--info"
          role="status"
          data-test="correct-banner"
        >
          <v-icon small color="#53389e">mdi-information-outline</v-icon>
          <span>Correcting {{ correctingLabel }}. {{ correctingLabel }} is voided when you publish this draft.</span>
        </div>

        <PayRangeIdentitySummary
          :identity="identity"
          :job-family-level="levelLabel"
          :pay-basis-options="payBasisOptions"
          :work-mode-options="workModeOptions"
          hint="Read-only · set when the pay range was created"
        />

        <PayRangeVersionTimeline
          v-if="timelineVersions.length"
          compact
          :versions="timelineVersions"
          :currency-symbol="currencySymbol"
          data-test="version-timeline"
        />

        <div class="pay-range-version-drawer__section">
          <EffectiveDateToEffectiveTo
            :effective-from="effectiveFrom"
            :effective-to-enabled="false"
            :effective-to-read-only-label="effectiveToLabel"
            :hint="effectiveFromHint"
            :error="effectiveFromMessage"
            @update:effectiveFrom="onEffectiveFromChange"
          />
        </div>

        <div class="pay-range-version-drawer__section">
          <PayRangeAmountsEditor
            :min-amount="minAmount"
            :max-amount="maxAmount"
            :currency-symbol="currencySymbol"
            :currency-code="currencyCode"
            :decimal-places="decimalPlaces"
            :pay-basis="identity.payBasis || 'ANNUAL'"
            :state="amountsState"
            :suggestion="null"
            :sources="[]"
            :reasoning="sourcingReasoning"
            :phase="sourcingPhase"
            :stalled="sourcingStalled"
            :sourcing-started-at="sourcingStartedAt"
            @update:minAmount="onMinChange"
            @update:maxAmount="onMaxChange"
            @try-again="onSuggest"
            @enter-manually="onEnterManually"
          />
        </div>

        <template v-if="suggestState === 'sourced' && suggestion">
          <PayRangeSuggestionCard
            state="applied"
            :suggested-min="suggestion.min"
            :suggested-max="suggestion.max"
            :median="suggestion.median"
            :excluded-sources="excludedSources"
            data-test="suggestion-card"
            @undo="onUndoSuggestion"
          />
          <PayRangeMarketDataTable :sources="tableSources" :currency="currencyCode" data-test="market-data-table" />
        </template>

        <div
          v-if="showNoChangeAlert"
          class="pay-range-version-drawer__alert pay-range-version-drawer__alert--warning"
          role="alert"
          data-test="no-change-alert"
        >
          <v-icon small color="#93370d">mdi-alert-outline</v-icon>
          <span>{{ noChangeMessage }}</span>
        </div>
        <div
          v-if="saveError"
          class="pay-range-version-drawer__alert pay-range-version-drawer__alert--error"
          role="alert"
          data-test="save-error"
        >
          <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
          <span>{{ saveError }}</span>
        </div>
        <div
          v-if="publishError"
          class="pay-range-version-drawer__alert pay-range-version-drawer__alert--error"
          role="alert"
          data-test="publish-error"
        >
          <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
          <span>{{ publishError }}</span>
        </div>
        <div
          v-if="savedMessage"
          class="pay-range-version-drawer__alert pay-range-version-drawer__alert--success"
          role="status"
          data-test="saved-alert"
        >
          <v-icon small color="#067647">mdi-check-circle-outline</v-icon>
          <span>Draft saved. Nothing changes for positions until you publish.</span>
        </div>
      </div>

      <PayRangeDraftActionBar
        variant="version"
        :step="2"
        :state="drawerState"
        :amounts-valid="amountsValid"
        :suggest-enabled="featureEnabled"
        :publishing="publishing"
        :saving="saving"
        :publish-disabled="publishBlocked"
        :save-disabled="noChange"
        @discard="onDiscardDraft"
        @suggest="onSuggest"
        @save-draft="onSave"
        @publish="onPublish"
        @stop-sourcing="onStopSourcing"
      />
    </div>

    <PayRangeUnsavedChanges
      :value="unsavedDialog"
      @input="unsavedDialog = $event"
      @keep-editing="unsavedDialog = false"
      @discard="onDiscardUnsaved"
    />

    <DiscardDraftDialog
      :value="discardDialog"
      @input="discardDialog = $event"
      @onAction="onConfirmDiscardDraft"
    />
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "PayRangeVersionProposalDrawer",
};
</script>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PayRangeDrawerHeader from "@components/PayRangeDrawerHeader/PayRangeDrawerHeader.vue";
import PayRangeDraftActionBar from "@components/PayRangeDraftActionBar/PayRangeDraftActionBar.vue";
import PayRangeIdentitySummary from "@components/PayRangeIdentitySummary/PayRangeIdentitySummary.vue";
import EffectiveDateToEffectiveTo from "@components/EffectiveDateToEffectiveTo/EffectiveDateToEffectiveTo.vue";
import PayRangeAmountsEditor from "@components/PayRangeAmountsEditor/PayRangeAmountsEditor.vue";
import PayRangeMarketDataTable from "@components/PayRangeMarketDataTable/PayRangeMarketDataTable.vue";
import PayRangeSuggestionCard from "@components/PayRangeSuggestionCard/PayRangeSuggestionCard.vue";
import PayRangeVersionTimeline from "@components/PayRangeVersionTimeline/PayRangeVersionTimeline.vue";
import PayRangeUnsavedChanges from "@components/PayRangeUnsavedChanges/PayRangeUnsavedChanges.vue";
import DiscardDraftDialog from "@components/DiscardDraftDialog/DiscardDraftDialog.vue";
import {
  getAccountId,
  suggestPayRange,
  createPayRangeVersion,
  editPayRangeVersion,
  correctPayRangeVersion,
  discardPayRangeVersion,
  publishPayRangeVersion,
} from "@/api/payRangeAPI";
import { getChatterList } from "@/api/chatterAPI";
import { payRangeApiToModalInitialValues, toSuggestApiRequest } from "@components/PayRangeModal/payRangeDraftPayload";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { localTodayIso } from "@components/PayRangeTimelineAndList/payRangeTimelineUtils";
import { formatFairPayDate, formatFairPayVersion } from "@components/utils/fairPayFormat";
import { useBodyPortalPointerTracker } from "@components/utils/useBodyPortalDropdown";

const CHATTER_TABLE = "pay_range_version";
const MAX_NOT_ABOVE_MIN_MESSAGE = "Max must exceed Min.";
const SAVE_FAILED_MESSAGE = "Save failed. Your entries are kept. Try again.";
const PUBLISH_FAILED_MESSAGE = "Publish failed. Your entries are kept. Try again.";

const props = defineProps({
  value: { type: Boolean, default: false },
  /** Full pay range API object, including `versions[]`. */
  payRange: { type: Object, default: () => ({}) },
  /** An existing Draft version to continue editing. */
  version: { type: Object, default: null },
  /** The In Force version being corrected: opens a prefilled Draft (Correct flow). */
  correctVersion: { type: Object, default: null },
  /** The version a new draft replaces; defaults to the live version in `payRange.versions`. */
  replacedVersion: { type: Object, default: null },
  payBasisOptions: { type: Array, default: () => [] },
  workModeOptions: { type: Array, default: () => [] },
  /** Overridable async handlers (Storybook / tests). Default to the real API. */
  suggest: { type: Function, default: null },
  saveDraft: { type: Function, default: null },
  publish: { type: Function, default: null },
  discard: { type: Function, default: null },
});

const emit = defineEmits(["update:value", "close", "save", "publish", "discard", "chatter"]);

const { proxy } = getCurrentInstance() || {};

const identity = ref({});
const minAmount = ref(null);
const maxAmount = ref(null);
const effectiveFrom = ref(null);
const draftId = ref(null);
const suggestion = ref(null);
const suggestSources = ref([]);
const previousAmounts = ref(null);
const chatterCount = ref(0);

const suggestState = ref("idle");
const sourcingReasoning = ref("");
const sourcingPhase = ref("");
const sourcingStalled = ref(false);
const sourcingStartedAt = ref(null);
const saveError = ref("");
const publishError = ref("");
const savedMessage = ref(false);
const effectiveFromError = ref("");
const serverDateConflict = ref(null);
const serverNoChange = ref(false);
const saving = ref(false);
const publishing = ref(false);
const dirty = ref(false);
const edited = ref(false);
const drawerOpen = ref(props.value);
const unsavedDialog = ref(false);
const { consumePortalPointer } = useBodyPortalPointerTracker();
const discardDialog = ref(false);
const featureEnabled = ref(false);

let abortController = null;
let seeded = false;

const payRangeId = computed(() => props.payRange?.id ?? props.payRange?.pay_range_id ?? null);
const versions = computed(() => (Array.isArray(props.payRange?.versions) ? props.payRange.versions : []));

const correctTarget = computed(() => {
  if (props.correctVersion) return props.correctVersion;
  const origin = props.version?.correction_of;
  if (origin == null) return null;
  return versions.value.find((v) => v.id === origin) || null;
});

const replaced = computed(() => {
  if (correctTarget.value) return correctTarget.value;
  if (props.replacedVersion) return props.replacedVersion;
  const live = versions.value.filter((v) => v.version_number != null && !v.voided_at);
  return (
    live.find((v) => v.active) ||
    [...live].sort((a, b) => Number(b.version_number) - Number(a.version_number))[0] ||
    null
  );
});

const correcting = computed(() => correctTarget.value != null);
const replacedLabel = computed(() => versionLabel(replaced.value));
const correctingLabel = computed(() => (correcting.value ? versionLabel(correctTarget.value) : ""));

const versionCode = computed(() => {
  const code = props.payRange?.code || props.payRange?.pay_range_code;
  return code ? `${code}.?` : null;
});
const status = computed(() => versionStatusBadge("draft").label);
const stateBadge = computed(() => props.version?.state || "Future");
const periodLabel = computed(() => (effectiveFrom.value ? `From ${formatFairPayDate(effectiveFrom.value)}` : ""));

const currencyCode = computed(
  () => identity.value.operatingCurrency?.code || props.payRange?.operating_currency?.code || "USD"
);
const currencySymbol = computed(() => (currencyCode.value === "USD" ? "$" : `${currencyCode.value} `));
const decimalPlaces = computed(
  () => identity.value.operatingCurrency?.decimalPlaces ?? props.payRange?.operating_currency?.decimal_places ?? 2
);

const levelLabel = computed(() => {
  const level = props.payRange?.job_family_level;
  if (!level) return "";
  const name = String(level.name || "");
  const levelText = /^L\d+/i.test(name) ? name : `L${level.ordinal ?? ""} ${name}`.trim();
  const family = level.job_family?.name;
  return family ? `${family} — ${levelText}` : levelText;
});

const hasAmount = (v) => v != null && v !== "" && Number.isFinite(Number(v));
const bothAmountsEntered = computed(() => hasAmount(minAmount.value) && hasAmount(maxAmount.value));
const maxNotAboveMin = computed(() => bothAmountsEntered.value && Number(maxAmount.value) <= Number(minAmount.value));
const amountsValid = computed(() => bothAmountsEntered.value && !maxNotAboveMin.value);

const effectiveFromIso = computed(() => toApiDateString(effectiveFrom.value));

const dateConflict = computed(() => {
  if (serverDateConflict.value) return serverDateConflict.value;
  if (correcting.value || !replaced.value?.effective_from || !effectiveFromIso.value) return null;
  const previous = toApiDateString(replaced.value.effective_from);
  if (previous && effectiveFromIso.value <= previous) return dateConflictMessage(previous);
  return null;
});

const noChange = computed(() => {
  if (serverNoChange.value) return true;
  const base = replaced.value;
  if (!base || !amountsValid.value) return false;
  const sameAmounts =
    Number(minAmount.value) === Number(base.min_amount) && Number(maxAmount.value) === Number(base.max_amount);
  if (!sameAmounts) return false;
  if (!correcting.value) return true;
  return effectiveFromIso.value === toApiDateString(base.effective_from);
});
const noChangeMessage = computed(
  () => `No change. These amounts match ${replacedLabel.value || "the current version"}. Change Min or Max to publish.`
);

const showNoChangeAlert = computed(() => serverNoChange.value || (edited.value && noChange.value));

const publishBlocked = computed(() => Boolean(dateConflict.value) || noChange.value);
const effectiveFromMessage = computed(() => effectiveFromError.value || dateConflict.value || "");
const effectiveFromHint = computed(() =>
  replacedLabel.value ? `After ${replacedLabel.value} starts. ${replacedLabel.value} ends the day before on publish.` : ""
);
const effectiveToLabel = computed(() =>
  correctTarget.value?.effective_to ? formatFairPayDate(correctTarget.value.effective_to) : "Open-ended"
);

const drawerState = computed(() => {
  if (publishing.value) return "publishing";
  if (publishError.value) return "publish-failed";
  if (saveError.value) return "save-failed";
  if (savedMessage.value) return "saved";
  if (suggestState.value === "sourcing") return "sourcing";
  if (suggestState.value === "sourcing-stopped") return "sourcing-stopped";
  if (suggestState.value === "cannot-source") return "cannot-source";
  if (suggestState.value === "timed-out") return "timed-out";
  if (dateConflict.value) return "date-conflict";
  if (maxNotAboveMin.value) return "max-must-exceed-min";
  if (noChange.value) return "no-change";
  if (suggestState.value === "sourced") return "iq-suggestion";
  return "default";
});

const amountsState = computed(() => {
  if (["sourcing", "sourcing-stopped", "cannot-source", "timed-out"].includes(suggestState.value)) {
    return suggestState.value;
  }
  if (maxNotAboveMin.value) return "max-must-exceed-min";
  if (suggestState.value === "sourced") return "iq-suggestion";
  return "default";
});

const excludedSources = computed(() =>
  suggestSources.value
    .filter((s) => s.status && s.status !== "included")
    .map((s) => ({ name: s.name, reason: s.exclusion_reason || "excluded" }))
);

const tableSources = computed(() =>
  suggestSources.value.map((s) => ({
    name: s.name,
    min: s.min,
    median: s.median ?? null,
    max: s.max,
    asOf: s.data_as_of ?? null,
    status: s.status === "included" ? "included" : "outlier",
    scopeLevel: s.scope_level ?? suggestionScope.value,
  }))
);
const suggestionScope = ref(null);

const timelineVersions = computed(() => {
  const rows = versions.value
    .filter((v) => v.version_number != null)
    .map((v) => ({
      version: v.version_number,
      state: v.state ?? v.status,
      minAmount: v.min_amount,
      maxAmount: v.max_amount,
      effectiveFrom: v.effective_from,
      effectiveTo: v.effective_to,
      retired: Boolean(v.voided_at),
    }));
  if (rows.length && effectiveFromIso.value) {
    const next = Math.max(...rows.map((r) => Number(r.version) || 0)) + 1;
    rows.push({
      version: next,
      state: "draft",
      minAmount: hasAmount(minAmount.value) ? Number(minAmount.value) : null,
      maxAmount: hasAmount(maxAmount.value) ? Number(maxAmount.value) : null,
      effectiveFrom: effectiveFromIso.value,
      effectiveTo: null,
      retired: false,
    });
  }
  return rows;
});

function versionLabel(v) {
  return formatFairPayVersion(v?.version_number);
}

function dateConflictMessage(previousIso) {
  const label = replacedLabel.value || "The current version";
  return `Must be after ${label} starts on ${formatFairPayDate(previousIso)}. ${label} ends the day before on publish.`;
}

function seed() {
  const initial = payRangeApiToModalInitialValues(props.payRange || {});
  identity.value = {
    jobFamily: initial.jobFamily || null,
    jobFamilyLevelId: initial.jobFamilyLevelId ?? null,
    location: initial.location || null,
    operatingCurrency: initial.operatingCurrency || null,
    payBasis: initial.payBasis || null,
    workMode: initial.workMode || "UNSPECIFIED",
  };
  const source = props.version || props.correctVersion;
  const current = source ? null : replaced.value;
  minAmount.value = source ? (source.min_amount ?? null) : (current?.min_amount ?? null);
  maxAmount.value = source ? (source.max_amount ?? null) : (current?.max_amount ?? null);
  effectiveFrom.value = source ? (source.effective_from ?? null) : defaultEffectiveFrom(current);
  draftId.value = props.version?.id ?? null;
  edited.value = false;
  clearSuggestion();
  suggestState.value = "idle";
  saveError.value = "";
  publishError.value = "";
  savedMessage.value = false;
  effectiveFromError.value = "";
  serverDateConflict.value = null;
  serverNoChange.value = false;
  seeded = true;
  dirty.value = false;
  loadChatterCount();
}

function clearSuggestion() {
  suggestion.value = null;
  suggestSources.value = [];
  suggestionScope.value = null;
  previousAmounts.value = null;
}

async function loadChatterCount() {
  if (draftId.value == null) {
    chatterCount.value = 0;
    return;
  }
  try {
    const response = await getChatterList(getAccountId(), draftId.value, CHATTER_TABLE);
    chatterCount.value = response?.data?.count ?? response?.data?.results?.length ?? 0;
  } catch {
    chatterCount.value = 0;
  }
}

function onChatter() {
  emit("chatter", { versionId: draftId.value });
}

onMounted(seed);
onMounted(() => document.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  if (abortController) abortController.abort();
});
onMounted(async () => {
  try {
    if (proxy && typeof proxy.$getFlag === "function") {
      featureEnabled.value = (await proxy.$getFlag(LD_FLAG_IDEA_MDP_429, false)) === true;
    }
  } catch {
    featureEnabled.value = false;
  }
});
watch(() => [props.payRange, props.version, props.correctVersion], seed, { deep: true });
watch(
  () => props.value,
  (v) => {
    drawerOpen.value = v;
  }
);

function clearMessages() {
  saveError.value = "";
  publishError.value = "";
  savedMessage.value = false;
  serverNoChange.value = false;
}

function onEffectiveFromChange(v) {
  effectiveFrom.value = v;
  effectiveFromError.value = "";
  serverDateConflict.value = null;
  clearMessages();
  markDirty();
}

function onMinChange(v) {
  minAmount.value = v;
  clearMessages();
  markDirty();
}

function onMaxChange(v) {
  maxAmount.value = v;
  clearMessages();
  markDirty();
}

function markDirty() {
  if (!seeded) return;
  dirty.value = true;
  edited.value = true;
}

function onKeydown(event) {
  if (event.key !== "Escape" || !drawerOpen.value) return;
  if (unsavedDialog.value || discardDialog.value) return;
  requestClose();
}

function onDrawerInput(open) {
  if (open) {
    drawerOpen.value = true;
    return;
  }
  if (consumePortalPointer()) {
    // A picker option list portaled to <body> counts as "outside" for Vuetify; it is part of the form.
    drawerOpen.value = false;
    nextTick(() => {
      drawerOpen.value = true;
    });
    return;
  }
  if (!dirty.value) {
    close();
    return;
  }
  unsavedDialog.value = true;
  drawerOpen.value = false;
  nextTick(() => {
    drawerOpen.value = true;
  });
}

function requestClose() {
  if (dirty.value) {
    unsavedDialog.value = true;
    return;
  }
  close();
}

function onDiscardUnsaved() {
  unsavedDialog.value = false;
  dirty.value = false;
  close();
}

function close() {
  if (abortController) abortController.abort();
  drawerOpen.value = false;
  emit("update:value", false);
  emit("close");
}

function onDiscardDraft() {
  discardDialog.value = true;
}

async function onConfirmDiscardDraft() {
  discardDialog.value = false;
  if (abortController) abortController.abort();
  try {
    if (draftId.value != null) {
      const handler = props.discard || discardPayRangeVersion;
      await handler(payRangeId.value, draftId.value);
    }
  } catch {
    saveError.value = "Discard failed. Your draft is kept. Try again.";
    return;
  }
  dirty.value = false;
  emit("discard");
  close();
}

function sanitizeSuggestBody(body) {
  const out = {};
  Object.entries(body || {}).forEach(([key, value]) => {
    if (key === "range_name") return;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) return;
    out[key] = value;
  });
  return out;
}

function decimalsOf(n) {
  const s = String(n);
  const i = s.indexOf(".");
  return i < 0 ? 0 : s.length - i - 1;
}

function isUsableSuggestion(s) {
  if (!s) return false;
  const min = Number(s.min);
  const max = Number(s.max);
  if (s.min == null || s.max == null || !Number.isFinite(min) || !Number.isFinite(max)) return false;
  if (max <= min) return false;
  return decimalsOf(min) <= decimalPlaces.value && decimalsOf(max) <= decimalPlaces.value;
}

async function onSuggest() {
  suggestState.value = "sourcing";
  sourcingReasoning.value = "";
  sourcingPhase.value = "";
  sourcingStalled.value = false;
  sourcingStartedAt.value = Date.now();
  clearMessages();
  const controller = new AbortController();
  abortController = controller;
  const isCurrent = () => abortController === controller && !controller.signal.aborted;

  const body = sanitizeSuggestBody(
    toSuggestApiRequest({ ...identity.value, minAmount: minAmount.value, maxAmount: maxAmount.value })
  );
  try {
    const handler = props.suggest || suggestPayRange;
    const res = await handler(body, {
      signal: controller.signal,
      onThinking: (text) => {
        if (!isCurrent()) return;
        sourcingReasoning.value = text || "";
        sourcingStalled.value = false;
      },
      onPhase: (label) => {
        if (!isCurrent()) return;
        sourcingPhase.value = label || "";
        sourcingStalled.value = false;
      },
      onStalled: () => {
        if (isCurrent()) sourcingStalled.value = true;
      },
    });
    if (controller.signal.aborted) return;
    const data = res?.data ?? res ?? {};
    const list = Array.isArray(data.sources) ? data.sources : [];
    const suggested = data.suggestion || null;

    if (list.length === 0 || !isUsableSuggestion(suggested)) {
      clearSuggestion();
      suggestState.value = "cannot-source";
      return;
    }
    previousAmounts.value = { min: minAmount.value, max: maxAmount.value };
    suggestSources.value = list;
    suggestion.value = { min: suggested.min, median: suggested.median ?? null, max: suggested.max };
    suggestionScope.value = data.effective_scope_level ?? data.requested_scope_level ?? null;
    minAmount.value = suggested.min;
    maxAmount.value = suggested.max;
    markDirty();
    suggestState.value = "sourced";
  } catch (err) {
    suggestState.value = controller.signal.aborted || isAbort(err) ? "sourcing-stopped" : "timed-out";
  } finally {
    if (abortController === controller) abortController = null;
  }
}

function onStopSourcing() {
  if (abortController) abortController.abort();
  suggestState.value = "sourcing-stopped";
}

function onEnterManually() {
  suggestState.value = "idle";
  clearSuggestion();
}

function onUndoSuggestion() {
  if (previousAmounts.value) {
    minAmount.value = previousAmounts.value.min;
    maxAmount.value = previousAmounts.value.max;
  }
  clearSuggestion();
  suggestState.value = "idle";
  markDirty();
}

function buildBody() {
  const body = {
    min_amount: Number(minAmount.value),
    max_amount: Number(maxAmount.value),
    effective_from: effectiveFromIso.value,
  };
  if (suggestSources.value.length) body.sources = JSON.stringify(suggestSources.value);
  return body;
}

function extractDraftId(res) {
  const data = res?.data ?? res ?? {};
  const drafts = (Array.isArray(data.versions) ? data.versions : []).filter(
    (v) => v.version_number == null && String(v.status || "Draft").toLowerCase() === "draft"
  );
  if (!drafts.length) return null;
  return drafts.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a)).id;
}

async function persistDraft() {
  const body = buildBody();
  const ctx = { payRangeId: payRangeId.value, versionId: draftId.value, correctVersionId: correctTarget.value?.id ?? null };
  let res;
  if (props.saveDraft) {
    res = await props.saveDraft(body, ctx);
  } else if (draftId.value != null) {
    res = await editPayRangeVersion(payRangeId.value, draftId.value, body);
  } else if (correcting.value) {
    const { sources, ...correction } = body;
    res = await correctPayRangeVersion(payRangeId.value, correctTarget.value.id, {
      ...correction,
      effective_to: correctTarget.value.effective_to ?? null,
    });
  } else {
    res = await createPayRangeVersion(payRangeId.value, body);
  }
  const id = extractDraftId(res) ?? res?.data?.id ?? res?.id ?? null;
  if (draftId.value == null && id != null) {
    draftId.value = id;
    loadChatterCount();
  }
  return draftId.value;
}

function extractErrorCode(err) {
  const data = err?.response?.data;
  if (!data || typeof data === "string") return null;
  return data.code || data.error_code || data.error || data.detail?.code || null;
}

function applyServerCode(err) {
  const code = extractErrorCode(err);
  if (code === "PAY_RANGE_VERSION_MAX_NOT_ABOVE_MIN") return MAX_NOT_ABOVE_MIN_MESSAGE;
  if (code === "PAY_RANGE_VERSION_DATES_OUT_OF_RANGE") {
    const previous = err?.response?.data?.details?.previous_effective_from || replaced.value?.effective_from;
    serverDateConflict.value = dateConflictMessage(toApiDateString(previous));
    return "";
  }
  if (code === "PAY_RANGE_VERSION_NO_CHANGE") {
    serverNoChange.value = true;
    return "";
  }
  return null;
}

function requireEffectiveFrom() {
  if (effectiveFromIso.value) return true;
  effectiveFromError.value = "Effective from is required.";
  return false;
}

async function onSave() {
  // One write in flight at a time: a double-click must not create a second version draft.
  if (saving.value || publishing.value) return;
  if (!amountsValid.value || !requireEffectiveFrom()) return;
  saving.value = true;
  clearMessages();
  try {
    await persistDraft();
    savedMessage.value = true;
    dirty.value = false;
    emit("save", { versionId: draftId.value });
  } catch (err) {
    const mapped = applyServerCode(err);
    saveError.value = mapped === null ? SAVE_FAILED_MESSAGE : mapped;
  } finally {
    saving.value = false;
  }
}

async function onPublish() {
  if (publishing.value || saving.value) return;
  if (!amountsValid.value || publishBlocked.value || !requireEffectiveFrom()) return;
  publishing.value = true;
  clearMessages();
  try {
    const id = await persistDraft();
    if (id == null) throw new Error("draft id missing");
    const handler = props.publish || publishPayRangeVersion;
    await handler(payRangeId.value, id);
    dirty.value = false;
    emit("publish", { versionId: id });
    close();
  } catch (err) {
    const mapped = applyServerCode(err);
    if (mapped !== "") publishError.value = mapped === null ? PUBLISH_FAILED_MESSAGE : mapped;
  } finally {
    publishing.value = false;
  }
}

function isAbort(err) {
  if (!err) return false;
  return err.name === "AbortError" || err.code === "ERR_CANCELED" || /cancel/i.test(err.message || "");
}

/** Earliest date the backend accepts: the later of today and the day after the current version starts. */
function defaultEffectiveFrom(current) {
  const today = localTodayIso();
  const start = toApiDateString(current?.effective_from);
  if (!start) return current ? today : null;
  const [y, m, d] = start.split("-").map(Number);
  const dayAfter = localTodayIso(new Date(y, m - 1, d + 1));
  return dayAfter > today ? dayAfter : today;
}

function toApiDateString(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
</script>

<style scoped>
.pay-range-version-drawer {
  height: 100vh;
  background-color: #ffffff;
}
.pay-range-version-drawer__panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  gap: 24px;
}
.pay-range-version-drawer__body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pay-range-version-drawer__section {
  padding: 8px 0;
}
.pay-range-version-drawer__alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}
.pay-range-version-drawer__alert--error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b42318;
}
.pay-range-version-drawer__alert--success {
  background-color: #e8f5e9;
  border: 1px solid #4caf50;
  color: #067647;
}
.pay-range-version-drawer__alert--warning {
  background-color: #fffaeb;
  border: 1px solid #fedf89;
  color: #93370d;
}
.pay-range-version-drawer__alert--info {
  background-color: #f9f5ff;
  border: 1px solid #e9d7fe;
  color: #53389e;
}
</style>
